import { useState, useCallback, useEffect } from 'react';
import {
  login as apiLogin,
  register as apiRegister,
  type LoginRequest,
  type RegisterRequest,
  type User,
  type Organization,
} from '../api/client';
import { TOKEN_KEY } from '../utils/constants';

interface AuthState {
  user: User | null;
  token: string | null;
  organization: Organization | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

export function useAuth() {
  const [state, setState] = useState<AuthState>(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    const userJson = localStorage.getItem('vantage_user');
    const orgJson = localStorage.getItem('vantage_organization');
    const user = userJson ? (JSON.parse(userJson) as User) : null;
    const organization = orgJson ? (JSON.parse(orgJson) as Organization) : null;
    return {
      user,
      token,
      organization,
      isAuthenticated: !!token,
      isLoading: false,
      error: null,
    };
  });

  useEffect(() => {
    const handleStorage = () => {
      const token = localStorage.getItem(TOKEN_KEY);
      if (!token) {
        setState({ user: null, token: null, organization: null, isAuthenticated: false, isLoading: false, error: null });
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const login = useCallback(async (data: LoginRequest) => {
    setState((s) => ({ ...s, isLoading: true, error: null }));
    try {
      const res = await apiLogin(data);
      localStorage.setItem(TOKEN_KEY, res.token);
      localStorage.setItem('vantage_user', JSON.stringify(res.user));
      const org = (res as any).organization || null;
      if (org) localStorage.setItem('vantage_organization', JSON.stringify(org));
      setState({ user: res.user, token: res.token, organization: org, isAuthenticated: true, isLoading: false, error: null });
      return res;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Login failed';
      setState((s) => ({ ...s, isLoading: false, error: message }));
      throw err;
    }
  }, []);

  const registerUser = useCallback(async (data: RegisterRequest) => {
    setState((s) => ({ ...s, isLoading: true, error: null }));
    try {
      const res = await apiRegister(data);
      localStorage.setItem(TOKEN_KEY, res.token);
      localStorage.setItem('vantage_user', JSON.stringify(res.user));
      const org = (res as any).organization || null;
      if (org) localStorage.setItem('vantage_organization', JSON.stringify(org));
      setState({ user: res.user, token: res.token, organization: org, isAuthenticated: true, isLoading: false, error: null });
      return res;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Registration failed';
      setState((s) => ({ ...s, isLoading: false, error: message }));
      throw err;
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem('vantage_user');
    localStorage.removeItem('vantage_organization');
    setState({ user: null, token: null, organization: null, isAuthenticated: false, isLoading: false, error: null });
  }, []);

  return { ...state, login, register: registerUser, logout };
}
